import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { BuyerApiService } from '../../../shared/services/buyer-api/survey.service';
import { SupplierApiService } from '../../../shared/services/supplier-api/supplier-api.service';
import { FeasibilityService } from '../../services/feasibility/feasibility.service';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import {
  BlendSupplier,
  BuyerSurveyResponse,
  EachSupplier,
  FlexibilityType,
  GetSupplierBlendInterface,
  GroupTemplateSupplier,
  SubSupplierGroup,
  SupplierGroupTemplateResponseInterface,
  SupplierQuantity,
  SupplierResponseInterface,
  SurveyRequest,
} from '../../../shared/interfaces/survey.interface';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { LayoutConstants } from '../layout-constants';
import {
  FlexibilityPricesParams,
  SupplierFlexibilityType,
} from '../../../shared/interfaces/supplier-api.interface';
import { ModalService } from '@purespectrum1/ui/modal';
import { EditSupplierGroupModalComponent } from './edit-supplier-group-modal/edit-supplier-group-modal.component';
import { v4 as uuidv4 } from 'uuid';
import { SurveyMetaDataService } from '../../../shared/services/survey-meta-data/survey-meta-data.service';
import { Router } from '@angular/router';
import { DecimalPipe } from '@angular/common';
import {
  PurePriceCPI,
  Supplier,
} from '../../shared/interfaces/pricing.interface';
import { SurveyPayloadService } from '../../survey-payload.service';
import {
  FeasibilityData,
  GroupedByQuotaFeas,
  SurveyTopBarData,
} from '../../../shared/interfaces/survey-meta-data.interface';
import { SURVEY_STATUS } from '../../../shared/constants/survey-status';
import { AuthService } from '../../../shared/services/auth/auth.service';
import { User } from '../../../shared/interfaces/user.interface';
import { notifyMessage } from '../../../shared/constants/notify-message';
import { deepCopy } from '../../utils/deep-copy';
import { QualificationsService } from '../../services/qualification/qualifications.service';
import { AuthState } from '../../../shared/interfaces/auth.interface';
import { CpiService } from '../../services/cpi/cpi.service';
import { TruncateNumber } from '../../../create-surveys/utils/truncate-number';
import { FeasibilityDataService } from '../../services/feasibility/feasibility-data.service';
import { PricingService } from '../../services/pricing/pricing.service';
import { CompanyAndFeature } from '../../../shared/interfaces/buyer-config.interface';
import { NO_COMPANY } from '../../shared/ui/config-enabled/config-enable.constants';
import { BlendModalComponent } from './blend-modal/blend-modal.component';
import { ManageSuppliersService } from '../../../shared/services/manage-suppliers-service/manage-suppliers.service';
import { SupplierGroupInterface } from './edit-supplier-group-modal/edit-supplier-group-modal.component';
import { FLEXIBILITY_OPTIONS } from '../../constants/flexibility-options';
import {
  FeasibilityRequest,
  FeasibilityResponse,
} from '../../../shared/interfaces/feasibility.interface';
import { AllocationFactory } from '../../services/domain/allocation';
import { Suppliers } from '../../../shared/domain/suppliers';
import { filter, map } from 'rxjs/operators';
import { Constants } from '../../../shared/constants/create-survey.constants';
import { ConfirmationModalComponent } from '../../../shared/ui/confirmation-modal/confirmation-modal.component';
import { CountrySurveyData } from '../../shared/ui/multicountry/interface/multi-country.interface';
import { MultiCountryDraftService } from '../../services/multi-country-draft/multi-country-draft.service';
import { SupplierBlendAlerts } from '../../domain/supplier-blend-alerts';
import { MultiCountryService } from '../../shared/ui/multicountry/services/multi-country.service';
import { McSvpParentChildSegregator } from '../../../shared/domain/mc-svp-parent-child-segregator';
import { LocaleSorter } from '../../shared/ui/multicountry/domain/locale-sorter';
import { EventEmitterService } from '../../../shared/services/event-emitter/event-emitter.service';
import { CommonService } from '../../../shared/services/common-service/common-service';
import { CURRENCY_CODE } from '../../../shared/constants/currency-units';
import {
  CurrencyExchangeRate,
  CurrencyExchangeRateResponse,
} from '../../../shared/interfaces/create-surveys.interface';
import { ActivityLogsService } from '../../../shared/services/activity-logs/activity-logs.service';
import { HighCpiConfirmationActivityGenerator } from '../../../shared/domain/high-cpi-confirmation-activity-generator';
import { SurveyStatus } from '../../../shared/utils/survey-status';
import { AidaPayloadService } from '../../../shared/services/aida/aida-payload/aida-payload.service';
import { PurePriceOverride } from '../../services/domain/pure-price-override';

@Component({
  selector: 'ps-ui-edit-suppliers',
  templateUrl: './edit-suppliers.component.html',
  styleUrls: ['./edit-suppliers.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class EditSuppliersComponent implements OnInit, OnDestroy, OnChanges {
  @Input() surveyId!: number;
  @Input() eligibleSuppliers?: Array<Supplier>;
  @Input() showFooter?: boolean = true;
  @Input() loi?: number;
  @Input() ir?: number;
  @Input() lang?: string;
  @Input() fieldTime?: number;
  @Input() suppliers: SupplierResponseInterface[] = [];
  @Input() currencyExchangeRate: CurrencyExchangeRate;
  @Input() flexibilityPrice?: FlexibilityPricesParams;
  @Input() operatorOverrideCPI?: string;
  @Input() selectivePriceOveride?: boolean;
  @Input() isPurePriceApplied?: boolean;
  @Input() supplierBlendData?: GetSupplierBlendInterface[];
  @Input() surveyDetails = <BuyerSurveyResponse>{};
  @Input() completesNeeded: number = 0;
  @Input() isSurveyCreation: boolean = false;
  @Input() isInsights: boolean = false;
  @Input() ismcQbGlobalPriceOveride: boolean = false;
  private _priceMultiplier: number = 0;
  @Input() set auth(data: AuthState) {
    this._auth.setState(data);
  }
  @Output() cpiOverrideConfirmationChange = new EventEmitter<boolean>();
  @Output() avgCpiThreshold = new EventEmitter<number>();
  @Output() changeSupplierRates = new EventEmitter<boolean>();
  @Output() selectedMcSvpSurvey = new EventEmitter<CountrySurveyData>();
  private _subscriptions: Subscription = new Subscription();
  public tempSuppliers: SupplierResponseInterface[] = [];
  public selectedSupplierGroup: SupplierResponseInterface[] = [];
  public blendSupplierGroupEmpty: Array<{
    config_name: string;
    typeOfDropDownOption?: number;
    id: string;
    isNotSelectable?: boolean;
  }> = [];
  public blendSupplierGroups: {
    listOfOptions: Array<{
      isSelected: boolean;
      config_name: string;
      id: string;
      typeOfDropDownOption?: number;
      setItemTextColorToBlue?: boolean;
    }>;
    suppliers?: EachSupplier[];
    id?: string;
  } = { listOfOptions: [] };
  public cpiObject: SupplierResponseInterface[] = [];
  public enableGroupingButton: boolean = false;
  public totalAllocations: number = 0;
  public isFlexible: boolean = true;
  public showUngroupButton: boolean = false;
  public supplierIsSelect: boolean = false;
  public isDraftSurvey: boolean = false;
  public totalRemaining!: number;
  public totalFeasibility: number = 0;
  public suppliersCount!: number;
  public supplierGroup!: { groupName?: string; cpi?: number | string };
  public minimumGroupSize: number = 1;
  public individual_supplier: number = 1;
  public surveyCreationObject: any;
  surveyPauseThreshold: number = 0;
  manualOverrideMdl: boolean = false;
  supplierRates: boolean = false;
  manualScpiOverrideEnabled: boolean = false;
  operatorOverrideCpi!: string;
  disableOverrideCpiInput: boolean = true;
  manualOperatorOverride: boolean = false;
  isOperatorOverideEdit: boolean = false;
  maxCpiThershold: number = LayoutConstants.MAX_CPI_THRESHOLD;
  private _defaultFlexibility: number = LayoutConstants.DEFAULT_FLEXIBILITY;
  private _defaultAllocation: number =
    LayoutConstants.DEFAULT_SUPPLIER_ALLOCATION;
  suppliersMaxCpiCounter: number = 0;
  isCpiConfirmed: boolean = true;
  containerConfirmCPIBtn: boolean = false;
  containerSelectiveConfirmCPIBtn: boolean = false;
  inputSelectiveCPI: boolean = false;
  operatorOverrideMdl: number = 0;
  getBlendLabel: string = '';
  supplierFlex = { value: this._defaultFlexibility, isFlexible: true };
  manualAllocationEdit: boolean = false;
  loadingBlends: boolean = false;
  selectedBlend: GetSupplierBlendInterface | any;
  public isPriceMultiplierAdded: boolean = false;
  public isPurePriceRatesEnabled: boolean = false;
  public isSelectedCountrySurvey: boolean = false;
  disablesurveyPauseThreshold: boolean = false;
  showSupplierGroupingButton: boolean = false;
  public userType: string = '';
  public userDetails?: User = {} as User;
  public loggedInAsServiceBuyer: boolean = false;
  public showSupplierManualOverride: boolean = false;
  public cpiOverrideConfirmationState: boolean = true;
  public showSupplierCPIColumn: boolean = true;
  public isAidaEnabled: boolean = false;
  private _eligibleSuppliers: Supplier[] = [];
  private _rateCardSuppliers: Supplier[] = [];
  private _callUsingBy: boolean = false;
  public buyerconfigObject: CompanyAndFeature = {
    companyId: -1,
    configSetting: 'enablePurePriceRates',
  };
  public hidePricesAcordion: boolean = false;
  groupedSuppTemplates: any = [];
  selectedSuppGroupTemplete: any = [];
  private _unselectableFields: string[] = ['A', 'INPUT'];
  flexibilityStatus = [
    {
      key: 0,
      value: 'Global',
    },
    {
      key: 1,
      value: 'Selective',
    },
    {
      key: 2,
      value: 'None',
    },
  ];
  flexibilitySelectedType: FlexibilityType = FLEXIBILITY_OPTIONS.GLOBAL;
  public mcSpvChildsDropdownData: CountrySurveyData[] = [];
  public selectedMcSpvChild: CountrySurveyData;
  public mcQbOverrideMdl: boolean = false;
  public isMcSvpSurvey: boolean = false;
  private companyFx: number;
  public shouldShowAidaErrorMessage: boolean = false;
  public isNonDraftSurvey: boolean = false;

  constructor(
    private _buyerApiService: BuyerApiService,
    private _toaster: ToasterService,
    private _supplierApiService: SupplierApiService,
    private _modal: ModalService,
    private _surveyMetaDataService: SurveyMetaDataService,
    private _router: Router,
    private _feasibilityService: FeasibilityService,
    private _decimalPipe: DecimalPipe,
    private _surveyPayloadService: SurveyPayloadService,
    private _auth: AuthService,
    private _qualificationService: QualificationsService,
    private _cpiService: CpiService,
    private _feasibilityDataService: FeasibilityDataService,
    private _pricingService: PricingService,
    private _manageSuppliersService: ManageSuppliersService,
    private _activityLogsService: ActivityLogsService,
    private _multiCountryDraftService: MultiCountryDraftService,
    private _multiCountryService: MultiCountryService,
    private _eventEmitterService: EventEmitterService,
    private _commonService: CommonService,
    private _aidaPayloadService: AidaPayloadService
  ) {}

  private _setUpuserData() {
    this.userType = this._auth.userType;
    this.userDetails = this._auth.user;
    this.loggedInAsServiceBuyer = this._auth.isLoggedInAsServiceBuyer();
    this.showSupplierGroupingButton =
      this._auth.buyerConfig?.enableSupplierGrouping ||
      this.userType === 'operator';
    this.companyFx = this._auth.companyConfig?.fx || CURRENCY_CODE.US;
  }

  ngOnInit(): void {
    this.mcQbOverrideMdl = this.ismcQbGlobalPriceOveride || false;
    this._setUpuserData();

    this.companyFx = this._auth.companyConfig?.fx || CURRENCY_CODE.US;
    if (!this.currencyExchangeRate) {
      if (this.loggedInAsServiceBuyer && this.companyFx !== CURRENCY_CODE.US) {
        this._commonService.getCurrencyExchangeRate(this.companyFx).subscribe(
          (response: CurrencyExchangeRateResponse) => {
            this.currencyExchangeRate = response.currencies;
          },
          (error) => {
            this._toaster.error(error.error.msg);
          }
        );
      }
    }

    this._surveyMetaDataService
      .isManuallyEditedAllocationSaved()
      .subscribe((state) => {
        this.manualAllocationEdit = state;
      });
    this._subscriptions.add(
      this._surveyMetaDataService
        .getSupplierSpecificInfo()
        .subscribe((data) => {
          const { eligibleSuppliers = [] } = data;
          this._eligibleSuppliers = eligibleSuppliers;
        })
    );
    this._surveyMetaDataService
      .getSupplierOverrideValue()
      .subscribe((payload) => {
        this.manualScpiOverrideEnabled = payload || false;
      });
    if (this.surveyId) {
      this.getSurveyData(this.surveyId).then(() => {
        this.getSuppliers(this.surveyId).then(async () => {
          this.getSuppliersBlends();
          this.getPurepriceRateCardPricing();
        });
        this.getFlexibilityPricesData(this.surveyId);
        //TODO We need to re-think the way we are handling the suppliers and it informations because we change the supplier
        //in a lot of places and don't have this rules centralized in one place (like a supplierService)
        //If we have some completes remainning we should re-distribute the allocations
        if (this.totalRemaining > 0) {
          this._doSupplierAllocation();
        }
      });
    }
    this._manageSuppliersService
      .getAllGroupTemplates()
      .subscribe(
        (groupedSuppTemplates) =>
          (this.groupedSuppTemplates = groupedSuppTemplates.length
            ? groupedSuppTemplates
            : [{ groupName: 'No Group Templates' }])
      );
    this.isMcSvpSurvey = this._isMcSvpSurvey();
    this._handleMcSvpChildSurveyProcessing();
    this._handleMcSvpGlobalOverrideToAllCheckbox();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      !changes.suppliers?.firstChange &&
      changes.suppliers?.currentValue?.length
    ) {
      this.suppliers = deepCopy(changes.suppliers.currentValue);
      if (this.isPurePriceRatesEnabled) {
        this.setPurepriceRateCardPricing();
      } else {
        this.getSuppliers(this.surveyId);
        if (this.totalRemaining > 0) {
          this._doSupplierAllocation();
        }
      }
    }
  }

  private async _fetchAndSetMcSvpSurveys() {
    if (this.isMcSvpSurvey) {
      const surveys = await this._multiCountryService
        .getSurveys(this.surveyDetails)
        .toPromise();
      this._multiCountryDraftService.sendSpvSurveyResponseData(surveys);
    }
  }

  async getSurveyData(surveyId: number) {
    if (!this.surveyDetails || !Object.keys(this.surveyDetails).length) {
      const surveyData = await this._buyerApiService
        .getSurvey(surveyId, 1)
        .toPromise();
      if (
        surveyData.ps_survey_status === SURVEY_STATUS.DRAFT &&
        this.userType === 'operator' &&
        !this.loggedInAsServiceBuyer
      ) {
        this._router.navigateByUrl('/dashboard');
        return;
      }
      this.surveyDetails = surveyData;
      this.isMcSvpSurvey = this._isMcSvpSurvey();
      await this._fetchAndSetMcSvpSurveys();
      this._processSurveyData(surveyData);
      this._surveyMetaDataService.setSupplierOverrideValue(
        this.manualScpiOverrideEnabled
      );
      this._surveyMetaDataService.setPurePriceRateCardEnable(
        this.surveyDetails.is_pure_price_rate_card_enable
      );
      this.isPurePriceRatesEnabled =
        this.surveyDetails.is_pure_price_rate_card_enable;
      this.supplierRates = !this.surveyDetails?.is_pure_price_rate_card_enable;
      this._setSupplierpriceCardFlag();
    } else {
      this._processSurveyData(this.surveyDetails, false); //Create survey workflow
    }
    this._manageSuppliersService
      .getAllGroupTemplates()
      .subscribe(
        (groupedSuppTemplates) =>
          (this.groupedSuppTemplates = groupedSuppTemplates.length
            ? groupedSuppTemplates
            : [{ groupName: 'No Group Templates' }])
      );
    this._subscriptions.add(
      this._surveyMetaDataService
        .getPurePriceRateCardEnable()
        .subscribe((purePriceRateCardEnable) => {
          this.isPurePriceRatesEnabled = purePriceRateCardEnable;
          this.supplierRates = !purePriceRateCardEnable;
        })
    );
  }

  private _setupHeaderInformation(surveyData: BuyerSurveyResponse) {
    let totalClicks = 0;
    if (surveyData.click_balancing) {
      surveyData.click_balancing = surveyData.click_balancing ? 1 : 0;
      totalClicks = Math.round(
        (surveyData.completes_required * 100) / surveyData.expected_ir
      );
    }
    this._surveyPayloadService.addTotalClickInHeaderBar(
      surveyData,
      totalClicks
    );
  }

  private _processSurveyData(
    surveyData: BuyerSurveyResponse,
    distributeAllocations: boolean = true,
    isSetupHeaderInformation: boolean = true
  ) {
    this.buyerconfigObject = {
      companyId: this.userDetails?.cmp || NO_COMPANY,
      configSetting: 'enablePurePriceRates',
    };
    if (this.userType === 'operator') {
      this.buyerconfigObject = {
        companyId: surveyData.buyer_company ?? surveyData?.buyer_id,
        configSetting: 'enablePurePriceRates',
      };
    }
    if (!this.completesNeeded) {
      this.completesNeeded =
        surveyData.click_balancing == 0
          ? surveyData.completes_required!
          : surveyData.estimated_clicks!;
    }

    this.isAidaEnabled = !!this.surveyDetails.aida?.enabled;
    this.isNonDraftSurvey =
      this.surveyDetails.ps_survey_status !== SURVEY_STATUS.DRAFT;

    this._processFeasibility();
    if (distributeAllocations) {
      this._doSupplierAllocation();
    }
    if (isSetupHeaderInformation) {
      this._setupHeaderInformation(surveyData);
    }
    this._setUpOperatorOverridesFlags();
    this._setPurePriceRateCardData(surveyData);
  }

  async getSuppliers(surveyId: number, mcSVP = false) {
    if (!this.suppliers?.length || mcSVP) {
      const mode = 'editable'; // it will add missing suppliers in survey and survey management collections when GET/suppliers called
      const supplierDetails = await this._buyerApiService
        .getSuppliers(
          surveyId,
          mode,
          this.loi,
          this.ir,
          this.lang,
          this.fieldTime
        )
        .toPromise();
      this.suppliers = supplierDetails.map((supplier) => {
        supplier.isChecked =
          supplier.is_active === true && supplier.quantity > 0;
        return supplier;
      });
      this.flexibilitySelectedType = this.setFlexibilitySelectedType();
      this._fixSupplierCpiToDigits(2);
      this.tempSuppliers = this.suppliers || [];
      this._performSupplierCalculations();
    } else {
      this.flexibilitySelectedType = this.setFlexibilitySelectedType();
      this._fixSupplierCpiToDigits(2);
      this.tempSuppliers = this.suppliers;
      this._performSupplierCalculations();
    }
  }

  private _fixSupplierCpiToDigits(digits: number) {
    for (const supplier of this.suppliers) {
      supplier.cpi = parseFloat((supplier.cpi || 0).toFixed(digits));
      const cpiInUsd = this._convertCpiToUsd(supplier.cpi);
      const variableMargin =
        Math.round(
          (((cpiInUsd || 0) - (supplier.scpi || 0)) / (cpiInUsd || 0)) * 100
        ) || 0;
      supplier.variable_margin = Number(variableMargin);
    }
  }

  private _convertCpiToUsd(SCPI: number) {
    let usdCpi = SCPI;
    if (
      this.companyFx &&
      this.companyFx !== CURRENCY_CODE.US &&
      this.currencyExchangeRate
    ) {
      usdCpi = this._commonService.localToUSD(
        SCPI,
        this.currencyExchangeRate.rate,
        this.currencyExchangeRate.CRRate
      );
    }
    return usdCpi;
  }

  private _performSupplierCalculations() {
    const supplier = this.suppliers.find((_supplier) => _supplier.flexValue);
    if (supplier) {
      this.supplierFlex.value = supplier.flexValue;
    } else {
      this.supplierFlex.value = 0;
      this.supplierFlex.isFlexible = false;
    }
    this._setSupplierValidFlag();
    this.getSuppliersBlends();
    this.calculateSupplierMinMax();
    this._supplierUpdatePayload();
    this._processFeasibility();
  }

  getFlexibilityPricesData(surveyId: number, isMc: boolean = false) {
    if (!this.flexibilityPrice || isMc) {
      this._supplierApiService
        .getFlexibilityPricesParams(surveyId)
        .subscribe((resp: FlexibilityPricesParams) => {
          this.flexibilityPrice = resp;
          this._setSupplierOverrides(resp);
          this._supplierUpdatePayload();
        });
    } else {
      this._setSupplierOverrides(this.flexibilityPrice);
      this._supplierUpdatePayload();
    }
  }

  setPurepriceRateCardPricing() {
    if (this.supplierRates != this._callUsingBy) {
      this.suppliers = deepCopy(this.tempSuppliers);
      for (const sup of this.suppliers) {
        const rateSup = this._rateCardSuppliers.find(
          (s) => s.id === sup.suppliers[0].id
        );
        if (rateSup) {
          sup.cpi = rateSup.cpi;
          sup.block_price = rateSup.cpi;
          sup.scpi = rateSup.SCPI;
          sup.fee = rateSup.fee;
        }
      }
    } else {
      this.suppliers = this.tempSuppliers;
    }
    if (
      this.supplierRates &&
      this.surveyDetails?.is_pure_price_rate_card_enable
    ) {
      this._feasibilityDataService.updateSupplierCpi(
        this.suppliers,
        this._priceMultiplier,
        this.manualOverrideMdl
      );
    }
    this._supplierUpdatePayload();
    this.isPurePriceRatesEnabled = !this.supplierRates;
    this.changeSupplierRates.emit(this.supplierRates);
  }

  getPurepriceRateCardPricing() {
    // avoid useless requests when user config is disabled for PPv2
    if (
      this._auth.buyerConfig?.enablePurePriceRates ||
      this.surveyDetails?.aida?.enabled
    ) {
      this._subscriptions.add(
        this._surveyMetaDataService
          .getSurveyHeaderData()
          .subscribe(async (surveyTopBarData: SurveyTopBarData) => {
            const { location, expected_loi, expected_ir, incl_excl } =
              surveyTopBarData;
            if (location) {
              this._callUsingBy = this.supplierRates;

              const params = [
                location.countryCode,
                location.languageShort,
                expected_loi.toString(),
                expected_ir.toString(),
                incl_excl.toString(),
                this.supplierRates,
                surveyTopBarData.surveyFieldTime.toString(),
              ] as const;
              const response = await this._pricingService
                .getEligibleSuppliers(...params)
                .toPromise();
              this._rateCardSuppliers = response.suppliers;

              if (this.surveyDetails.aida?.enabled) {
                const payload = {
                  N: this.surveyDetails.completes_required,
                  ...(this.surveyDetails.ps_survey_status ===
                    SurveyStatus.Draft && {
                    gpp: {
                      LOI: expected_loi,
                      IR: expected_ir,
                    },
                  }),
                  fldTime: this.surveyDetails.field_time,
                  clickBalance: this.surveyDetails?.click_balancing || 0,
                  estmClicks: this.surveyDetails?.estimated_clicks || 0,
                  quotas: this.surveyDetails.quotas,
                  qualifications: this.surveyDetails.qualifications.filter(
                    (qualification) => qualification.isQuotaSet
                  ),
                };
                this._eligibleSuppliersDetails = await this._pricingService
                  .getPurePriceBuyerCpi(
                    this.surveyDetails.ps_survey_id,
                    payload
                  )
                  .toPromise();
              }
            }
          })
      );
    }
  }

  private _eligibleSuppliersDetails: PurePriceCPI;

  private _setSupplierOverrides(flexibilityPrice: FlexibilityPricesParams) {
    this.surveyPauseThreshold = flexibilityPrice.survey_pause_threshold || 0;
    this.avgCpiThreshold.emit(this.surveyPauseThreshold);

    this.manualOverrideMdl = !!this.selectivePriceOveride;
    if (flexibilityPrice?.manual_override?.toString()) {
      this.manualOverrideMdl = flexibilityPrice.manual_override;
      this.isOperatorOverideEdit = this.manualOverrideMdl;
      this.disableOverrideCpiInput = !this.manualOverrideMdl;
    }

    this.operatorOverrideCpi = '';
    if (this.operatorOverrideCPI) {
      this.operatorOverrideCpi = this.operatorOverrideCPI;
    } else if (flexibilityPrice.survey_price_override) {
      this.operatorOverrideCpi = String(flexibilityPrice.survey_price_override);
    }
    if (
      Number(this.operatorOverrideCpi) &&
      this.operatorOverrideCpi !== 'null'
    ) {
      this.disablesurveyPauseThreshold = true;
    }
    if (this.manualOverrideMdl) {
      this.isOperatorOverideEdit = true;
      this.disableOverrideCpiInput = false;
    }
    this._setSupplierpriceCardFlag();
    this.calculateSupplierMinMax();
  }

  openEditModal(editSupplier: SupplierResponseInterface) {
    this.selectedSupplierGroup = [];
    this.selectedSupplierGroup.push(editSupplier);
    this._modal.open(EditSupplierGroupModalComponent, {
      data: {
        surveyId: this.surveyDetails.ps_survey_id,
        selectedSupplierBlend: this.selectedSupplierGroup,
        suppliers: this.suppliers,
        tempSuppliers: this.tempSuppliers,
        completesNeeded: this.completesNeeded,
        editGroup: true,
        cpiOverrideEnabled: !!Number(this.operatorOverrideCpi),
        addSupplierGroup: (
          supplier: SupplierResponseInterface[],
          supplierGroup: SupplierGroupInterface
        ) => this.createGroup(supplier, supplierGroup),
      },
    });
  }

  calculateSupplierMinMax() {
    this.totalAllocations = 0;
    for (const supplier of this.suppliers) {
      if (supplier.flexValue === 0) {
        this.totalAllocations += supplier.quantity;
        supplier['flexValue'] = 0;
        supplier['isFlexibility'] = false;
        supplier['min'] = supplier.quantity;
        supplier['max'] = supplier.quantity;
        supplier['percentile'] =
          supplier.quantity > 0
            ? Math.floor((supplier.quantity * 100) / this.completesNeeded)
            : 0;
        supplier.completes_goal = supplier.max;
        // Case 2 when flexibility is 100%
      } else if (supplier.flexValue >= 100) {
        this.totalAllocations += supplier.quantity;
        supplier['percentile'] =
          supplier.quantity > 0
            ? Math.floor((supplier.quantity * 100) / this.completesNeeded)
            : 0;
        if (
          supplier.quantity > 0 &&
          supplier.quantity != this.completesNeeded
        ) {
          supplier['min'] = 0;
          supplier['max'] = this.completesNeeded;
        } else if (supplier.quantity == this.completesNeeded) {
          supplier['min'] = this.completesNeeded;
          supplier['max'] = this.completesNeeded;
        } else {
          supplier['min'] = 0;
          supplier['max'] = 0;
        }
        supplier.completes_goal = supplier.max;
        // Normal case of flexibility
      } else {
        let totalMin = 0;
        //first calculate min
        let allocation = supplier.quantity;
        allocation = Number(allocation) ? allocation : 0;
        supplier['percentile'] =
          supplier.quantity > 0
            ? Math.floor((supplier.quantity * 100) / this.completesNeeded)
            : 0;

        this.totalAllocations += supplier.quantity;
        // If total completes are allocated to single supplier the min ansd max are equal
        if (supplier.quantity == this.completesNeeded) {
          supplier['min'] = supplier.quantity;
        } else {
          let min = allocation - allocation * (supplier.flexValue / 100);
          min = Math.ceil(min > 0 ? min : 0);
          totalMin += min;
          supplier['min'] = min;
        }
        // Calculate max
        supplier['percentile'] =
          supplier.quantity > 0
            ? Math.floor((supplier.quantity * 100) / this.completesNeeded)
            : 0;
        // If total completes are allocated to single supplier the min ansd max are equal
        if (supplier.quantity == this.completesNeeded) {
          supplier['max'] = supplier.quantity;
        } else {
          let max1 = allocation + (allocation * supplier.flexValue) / 100;
          if (totalMin > this.completesNeeded) {
            supplier['max'] = supplier.quantity;
          } else {
            supplier['max'] = Math.floor(Math.min(max1, this.completesNeeded));
          }
        }
        supplier.completes_goal = supplier.max;
      }
    }
    this.totalRemaining = this.completesNeeded - this.totalAllocations;
    const threshold = this._calculateSurveyPauseThreshold();

    if (this.surveyPauseThreshold < threshold) {
      this.surveyPauseThreshold = threshold;
    }

    this._supplierUpdatePayload();
  }

  setFlexibilityType(event: SupplierFlexibilityType) {
    this.removeBlends();
    if (event.key === 0) {
      this.supplierFlex.value = 100;
      this.supplierFlex.isFlexible = true;
      this.updateFlexValue(this.supplierFlex.value);
    } else if (event.key === 2) {
      this.supplierFlex.value = 0;
      this.updateFlexValue(this.supplierFlex.value);
    }
  }

  allSuppliersAreZeroFlexibility() {
    return this.suppliers.every((supp) => {
      return supp.flexValue === 0;
    });
  }

  allSuppliersAreTheSameFlexibility() {
    const selectedSuppliers = this.suppliers.filter(
      (supplier) => supplier.isChecked
    );
    const fristFlexValue = selectedSuppliers[0].flexValue;
    let allSuppliersAreTheSameFlexibility = false;
    if (fristFlexValue != 0) {
      allSuppliersAreTheSameFlexibility = selectedSuppliers.every((supp) => {
        return supp.flexValue === fristFlexValue;
      });
    }
    return allSuppliersAreTheSameFlexibility;
  }

  setFlexibilitySelectedType(): FlexibilityType {
    if (this.allSuppliersAreTheSameFlexibility()) {
      return FLEXIBILITY_OPTIONS.GLOBAL;
    } else if (this.allSuppliersAreZeroFlexibility()) {
      return FLEXIBILITY_OPTIONS.NONE;
    } else {
      return FLEXIBILITY_OPTIONS.SPECIFIC;
    }
  }

  updateSupplierFlexibility(index: number) {
    const selectedSupp = this.suppliers[index];
    const flexValue = Math.floor(selectedSupp.flexValue || 0);
    const selectedSuppliers = this.suppliers.filter(
      ({ setActive }) => setActive
    );
    if (!selectedSuppliers.includes(selectedSupp)) {
      selectedSuppliers.push(selectedSupp);
    }
    selectedSuppliers.forEach((supplier) => {
      supplier.flexValue = flexValue;
    });
    this.calculateSupplierMinMax();
    this.removeBlends();
    this._supplierUpdatePayload();
  }

  updateSupplierCpi(index: number) {
    const selectedSupp = this.suppliers[index];
    const supplierCpi = selectedSupp.cpi || 0;
    const selectedSuppliers = this.suppliers.filter(
      ({ setActive }) => setActive
    );
    if (!selectedSuppliers.includes(selectedSupp)) {
      selectedSuppliers.push(selectedSupp);
    }
    selectedSuppliers.forEach((supplier) => {
      supplier.cpi = supplierCpi;
    });
    this._supplierUpdatePayload();
  }

  /**
   @param {number} flexValue  use to update flexValue for supplier
   */
  updateFlexValue(flexValue: number) {
    this.suppliers.forEach((supp) => {
      supp.flexValue = flexValue;
    });
    this.calculateSupplierMinMax();
  }

  selectSupplierRow(
    event: MouseEvent,
    selectedSupplier: SupplierResponseInterface
  ) {
    if (!this._unselectableFields.includes((event.target as Element).tagName)) {
      selectedSupplier.setActive = !selectedSupplier.setActive;
      this.selectSupplierGroup(selectedSupplier);
    }
  }

  selectSupplierGroup(selectedSupplier?: SupplierResponseInterface) {
    let supplierGroupCount = 0;
    this.enableGroupingButton = false;
    this.selectedSupplierGroup = [];
    this.suppliers.forEach((supplier) => {
      if (supplier.setActive && supplier.setActive === true) {
        supplierGroupCount++;
        this.selectedSupplierGroup.push(supplier);
      }
      if (supplierGroupCount >= 2) {
        this.enableGroupingButton = true;
      } else {
        this.enableGroupingButton = false;
      }
    });
    if (!this.selectedSupplierGroup.length) {
      this.showUngroupButton = false;
    }
    this.selectedSupplierGroup.forEach((supplier) => {
      this.showUngroupButton =
        this.selectedSupplierGroup.length === 1 &&
        supplier.group_name !== '' &&
        supplier.group_name !== null
          ? true
          : false;
    });
    // get grouped supplier from selected suppliers.
    const groupedSupplier = this.selectedSupplierGroup.find(
      (supplier) => supplier.group_name
    );
    if (groupedSupplier && selectedSupplier) {
      this.groupedSupplierSelected(selectedSupplier, groupedSupplier);
    } else {
      // if no grouped suppliers are selected then remove the groupName field.
      this.supplierGroup = {};
    }
    this._supplierUpdatePayload();
  }

  groupedSupplierSelected(
    selectedSupplier: SupplierResponseInterface,
    groupedSupplier: SupplierResponseInterface
  ) {
    if (!this.supplierGroup) {
      this.supplierGroup = {};
    }
    if (selectedSupplier.setActive) {
      // check if currently checked supplier is added or removed. if added `setActive`=true
      if (!this.supplierGroup.groupName) {
        // check if groupName is already set, if two supplier groups are selected then groupName should show the first selected group.
        this.supplierGroup.groupName = selectedSupplier.group_name;
        this.supplierGroup.cpi = selectedSupplier.cpi;
      }
    } else {
      // if currently checked supplier is removed then set groupName field to next selected grouped supplier.
      this.supplierGroup.groupName = groupedSupplier.group_name;
      this.supplierGroup.cpi = groupedSupplier.cpi;
    }
  }

  //Change the localization of this functions to be close to the suppliers blends functions
  getSuppliersBlends() {
    // Invoke the service only if supplierBlendData is null
    if (!this.supplierBlendData || !this.supplierBlendData.length) {
      this.loadingBlends = true;
      const locale = this.surveyDetails?.locale
        ? `${this.surveyDetails.locale?.language_translate}_${this.surveyDetails.locale?.country_code}`
        : null;
      this._buyerApiService
        .getSupplierBlends(locale)
        .subscribe((supplierBlendData) => {
          this._processSupplierBlendData(supplierBlendData);
          this.loadingBlends = false;
        });
    } else {
      this._processSupplierBlendData(this.supplierBlendData);
    }
  }

  private _processSupplierBlendData(
    supplierBlendData: GetSupplierBlendInterface[]
  ) {
    if (supplierBlendData.length) {
      let mappedBlends = {
        // Filtering blends that have at least 2 of the available suppliers
        listOfOptions: supplierBlendData
          .filter((blendToFilter) => {
            let includeBlend = false;
            blendToFilter.suppliers.forEach((suppFromBlend) => {
              let surveySuplrsInBlend: number = 0;
              this.suppliers.forEach((scopeSupplier) => {
                scopeSupplier.suppliers.forEach((subSupplier) => {
                  if (
                    this._manageSuppliersService
                      .getBlendSupplierIds(suppFromBlend)
                      .includes(subSupplier.id)
                  ) {
                    surveySuplrsInBlend++;
                  }
                });
              });
              // Validate if at least 1 individual or group in the blend are able to be applied in the survey
              if (this.validateGroupBlend(suppFromBlend, surveySuplrsInBlend)) {
                includeBlend = true;
              }
            });
            return includeBlend;
            // After filtering, is added an attribute to know which blend is selected in the dropdown
          })
          .map((blend) => {
            return { ...blend, isSelected: false };
          }),
      };
      this.blendSupplierGroups = mappedBlends;
      if (this.surveyDetails && this.surveyDetails.blend_id) {
        // set selected blend
        this.selectedBlend = mappedBlends.listOfOptions.find(
          (blends) => blends.id === this.surveyDetails.blend_id
        );
      } else {
        this.selectedBlend = null;
      }
      if (
        !this.blendSupplierGroups.listOfOptions.length &&
        this.isSurveyCreation
      ) {
        this.pushDefaultBlends();
      } else {
        if (this._auth.user?.buyerAcssLvls === 'superAdmin') {
          this.blendSupplierGroups.listOfOptions.unshift({
            id: '5',
            config_name: '-- Save as Supplier Blend --',
            isSelected: false,
            typeOfDropDownOption: 5,
            setItemTextColorToBlue: true,
          });
        }
        this.blendSupplierGroups.listOfOptions.unshift({
          id: '4',
          config_name: '-- Remove Supplier Blend --',
          isSelected: false,
          typeOfDropDownOption: 4,
          setItemTextColorToBlue: true,
        });
      }
    } else {
      this.pushDefaultBlends();
    }
  }

  pushDefaultBlends() {
    if (!this.blendSupplierGroupEmpty.length) {
      this.blendSupplierGroupEmpty.push({
        config_name: 'No Supplier Blends Saved',
        typeOfDropDownOption: 2,
        id: '2',
        isNotSelectable: true,
      });
      this.blendSupplierGroupEmpty.push({
        config_name: 'Click here to create Supplier Blends',
        typeOfDropDownOption: 3,
        id: '3',
      });
    }
  }

  validateGroupBlend(
    suppFromBlend: BlendSupplier,
    surveySuplrsInBlend: number
  ) {
    return (
      suppFromBlend.supplier_id &&
      ((suppFromBlend.supplier_id.length >= this.minimumGroupSize &&
        surveySuplrsInBlend >= this.minimumGroupSize) ||
        (suppFromBlend.supplier_id.length === this.individual_supplier &&
          surveySuplrsInBlend))
    );
  }

  /**
   * @param {Object} blendSupplierGroups the blend object selected in the dropdown list
   * TODO this code it's duplicated, we need to adress it in PD-11190
   */
  selectBlend(event: GetSupplierBlendInterface) {
    const doNothing = '2';
    const redirect = '3';
    const resetBlends = '4';
    const saveBlend = '5';
    if (event.id === doNothing) {
      return;
    }
    if (event.id === saveBlend) {
      this.openBlendModal();
      return;
    }
    if (event.id === redirect) {
      const url = this._router.serializeUrl(
        this._router.createUrlTree([`buyer/manage-suppliers`], {
          fragment: 'supplier-blend',
        })
      );
      window.open(url, '_blank');
      return;
    }
    this.unGroupAll();
    this.selectedBlend = event;
    this._patchSelectedBlendInMcSvpChildSurvey();
    if (event.id === resetBlends) {
      this._resetFlexibility();
      this.distributeSuppliers();
      this.tempSuppliers = this.suppliers;
      return;
    }
    event.suppliers.forEach((suppFromBlend) => {
      // Check if this supplier from the blend is a group
      if (
        suppFromBlend.supplier_id &&
        suppFromBlend.supplier_id.length > this.individual_supplier
      ) {
        let suppsAvailableForGrouping = 0;
        // Check how many suppliers from the blend are available in the survey list
        this.suppliers.forEach((scopeSupplier) => {
          scopeSupplier.suppliers.forEach((suppFromSupplier) => {
            if (
              this._manageSuppliersService
                .getBlendSupplierIds(suppFromBlend)
                .includes(suppFromSupplier.id)
            ) {
              suppsAvailableForGrouping++;
            }
          });
        });
        // Creating the group in the survey
        if (suppsAvailableForGrouping >= this.minimumGroupSize) {
          this.createSuppGroupFromBlend(suppFromBlend);
        }
      }
    });
    this._applyBlendAllocation(event);
    this._setAsSelected(event);
    this.setOperatorOverrideCpi();
    this._supplierUpdatePayload();
    this._applyBlendFlexibility(event);
    this.tempSuppliers = this.suppliers;
  }

  private _patchSelectedBlendInMcSvpChildSurvey() {
    if (!this.isMcSvpSurvey) {
      return;
    }

    const resetBlendId = '4';
    const selectedBlendId =
      this.blendId(this.selectedBlend) === resetBlendId
        ? ''
        : this.blendId(this.selectedBlend);
    this._sendToPatchMcSvpDataInCache({ blend_id: selectedBlendId });
  }

  private _sendToPatchMcSvpDataInCache(
    update: Partial<Omit<BuyerSurveyResponse, 'survey_localization'>>
  ) {
    const cacheUpdate = {
      for: 'each' as const,
      data: [
        {
          survey_localization: this.surveyDetails.survey_localization,
          ...update,
        },
      ],
    };
    this._multiCountryDraftService.sendMcSpvUpdateInCacheCommand(cacheUpdate);
  }

  /**
   * Ungroups all suppliers from the suppliers list in the survey
   */
  unGroupAll() {
    //Filter only suppliers that are groups
    const existingSupplierGroups = this.suppliers.filter(
      (suppFilterGroup) =>
        suppFilterGroup.group_name !== '' && suppFilterGroup.group_name !== null
    );

    existingSupplierGroups.forEach((groupToUngroup) => {
      const groupIndex = this.suppliers.findIndex(
        (supp) => supp.group_name === groupToUngroup.group_name
      );
      if (groupIndex !== undefined && groupIndex >= 0) {
        const ungroupedSuppliers = this.deleteGroup(groupToUngroup);
        this.suppliers.splice(groupIndex, 1, ...ungroupedSuppliers);
        this.calculateFlexibility();
      }
    });
    this._supplierUpdatePayload();
  }

  calculateFlexibility() {
    //TODO
  }

  private _setAllocationSuppliers(
    blendSuppliers: SubSupplierGroup[],
    suppliers: EachSupplier[]
  ) {
    if (suppliers?.length) {
      blendSuppliers.forEach(({ id: sId, allocation }) => {
        const supChange = suppliers.find(({ id }) => id === sId);
        if (supChange) {
          supChange.allocation = allocation;
        }
      });
    }
  }

  createSuppGroupFromBlend(suppFromBlend: BlendSupplier) {
    const suppliersToGroup = this.suppliers.filter((scopeSupplier) =>
      this._manageSuppliersService
        .getBlendSupplierIds(suppFromBlend)
        .includes(scopeSupplier.suppliers[0].id)
    );
    const flatSuppliers = suppliersToGroup?.flatMap(
      (s): EachSupplier[] => s.suppliers
    );
    this._setAllocationSuppliers(suppFromBlend.supplier_id, flatSuppliers);
    this._distributeRemainingAllocationToSurveySuppliers(flatSuppliers);
    const cpiAvg = this.averageCPI(suppliersToGroup);
    const groupParams: SupplierGroupInterface = {
      cpi: cpiAvg,
      groupName: suppFromBlend.group_name,
      isCustomAllocation: false,
    };
    this.addSupplierGroup(suppliersToGroup, groupParams);
  }

  private _distributeRemainingAllocationToSurveySuppliers(
    suppliersInSurvey: EachSupplier[]
  ) {
    const totalSubAllocation = suppliersInSurvey.reduce(
      (total, { allocation = 0 }) => total + allocation,
      0
    );
    if (totalSubAllocation >= 100 && totalSubAllocation != 0) {
      // nothing to distribute
      return;
    }
    let remainingAllocation = 100 - totalSubAllocation;

    while (remainingAllocation) {
      suppliersInSurvey.forEach((s) => {
        if (s.allocation && s.allocation < 100) {
          if (remainingAllocation) {
            s.allocation++;
            remainingAllocation--;
          }
        }
      });
    }
  }

  /**
   * @param {Array} suppliers array with suppliers
   * @returns average CPI of the suppliers
   */
  averageCPI(suppliers: SupplierResponseInterface[]) {
    return !!Number(this.operatorOverrideCpi)
      ? Number(this.operatorOverrideCpi)
      : suppliers.reduce(
          (cumulativeCPI: number, { cpi }) => cumulativeCPI + cpi,
          0
        ) / suppliers.length;
  }

  _ungroupOverlappingSupplierGroups(
    selectedTemplate: SupplierGroupTemplateResponseInterface
  ) {
    const selectedSupplierIds = selectedTemplate.suppliers.map((sup) => sup.id);
    this.suppliers.forEach((supp) => {
      const groupSupIds: number[] = supp.suppliers.map((s) => s.id);
      const commonIds = groupSupIds.filter((value) =>
        selectedSupplierIds.includes(value)
      );
      if (groupSupIds.length > 1 && commonIds.length) {
        supp.setActive = true;
        this.selectSupplierGroup(supp);
      }
    });
    this.ungroupSupplierGroup();
  }

  selectSupplierGroupTemplate(
    selectedTemplate: SupplierGroupTemplateResponseInterface
  ) {
    this._ungroupOverlappingSupplierGroups(selectedTemplate);
    this.showUngroupButton = false;
    selectedTemplate.suppliers.forEach((sup) => {
      sup.suppliers = [
        {
          id: sup.id,
          name: sup.name,
          allocation: sup.allocation,
        },
      ];
    });
    const selectedSuppliers =
      selectedTemplate.suppliers as SupplierResponseInterface[];
    const supplierGroup = {
      isCustomAllocation: selectedTemplate.is_custom_allocation || false,
      groupName: selectedTemplate.groupName,
      cpi: this._getCPIforSelectedGroupTemplate(selectedTemplate.suppliers),
    };
    this.addSupplierGroup(selectedSuppliers, supplierGroup);
    this.removeBlends();
  }

  _getCPIforSelectedGroupTemplate(selectedSuppliers: GroupTemplateSupplier[]) {
    const selectedSupplierIds = selectedSuppliers.map((sup) => sup.id);
    const supArrWithCpi = this.eligibleSuppliers?.filter((sup) =>
      selectedSupplierIds.includes(sup.id)
    );
    const totalCpi = supArrWithCpi?.reduce((acc, supp) => acc + supp.cpi, 0);
    return (totalCpi || 0) / selectedSupplierIds.length;
  }

  createGroup(
    supplier: SupplierResponseInterface[],
    supplierGroup: SupplierGroupInterface
  ) {
    this.removeBlends();
    this.addSupplierGroup(supplier, supplierGroup);
    this._supplierUpdatePayload();
  }

  addSupplierGroup(
    supplier: SupplierResponseInterface[],
    supplierGroup: SupplierGroupInterface
  ) {
    if (!supplier) {
      return;
    }
    const suppliersForAllocation = supplier.flatMap((s) => s.suppliers);
    const suppliersBlend = suppliersForAllocation.map((supplier) => {
      return {
        id: supplier.id,
        allocation: supplier.allocation || 0,
      };
    });

    this._setAllocationSuppliers(
      suppliersBlend,
      this.suppliers.flatMap((s) => s.suppliers)
    );
    const groupSupIds: number[] = supplier
      .flatMap((s) => s.suppliers)
      .map(({ id }) => id);
    if (!supplierGroup.cpi) {
      supplierGroup.cpi = this.cpiAverageForGroupedSupp(supplier);
    }
    const groupPayload: SupplierResponseInterface[] = this._createGroupObj(
      this.suppliers,
      { ...supplierGroup, groupSupIds }
    );
    this.suppliers = groupPayload;
    this._fixSupplierCpiToDigits(2);
    this.supplierGroup = {};
    this.calculateSupplierMinMax();
    this._supplierUpdatePayload();
    this.tempSuppliers = this.suppliers;
  }

  cpiAverageForGroupedSupp(suppliers: SupplierResponseInterface[]) {
    const total = this.tempSuppliers
      .filter(({ suppliers: [{ id }] }) => suppliers.some((s) => s.id === id))
      .reduce((total, { cpi }) => cpi + total, 0);

    return total / suppliers.length;
  }

  checkValidGroupName(
    supplier: SupplierResponseInterface[],
    supplierGroup: { cpi: number; groupName: string }
  ) {
    const isGroupExits = supplier.filter(
      (supp) => supp.group_name === supplierGroup.groupName
    );
    if (!isGroupExits.length) {
      const isExitsGroupName = this.suppliers.filter(
        (supp) => supp.group_name === supplierGroup.groupName
      );
      return isExitsGroupName.length ? true : false;
    }
    return false;
  }

  /**
   *
   * @param {Array} suppliersIndex Array containing all suppliers indexes to distribute the remaining completes
   */
  distributeRemainingAllocations(suppliersIndex: number[]) {
    let remaningCompletes = this.completesNeeded - this.totalAllocations;
    if (suppliersIndex.length > 0 && remaningCompletes > 0) {
      let allocationQty = 1;
      while (remaningCompletes > 0) {
        suppliersIndex.forEach((index: number) => {
          if (remaningCompletes > 0) {
            this.suppliers[index].quantity =
              this.suppliers[index].quantity + allocationQty;
            this.totalAllocations = this.totalAllocations + allocationQty;
            remaningCompletes = remaningCompletes - allocationQty;
          }
        });
      }
      suppliersIndex.forEach((suppIndx: number) =>
        this.allocationValueUpdate(suppIndx)
      );
    }
  }

  /**
   * 	Remove selected from the blend object to not show de check icon in the dropdown
   */
  removeSelected() {
    const blendIndex = this.blendSupplierGroups.listOfOptions.findIndex(
      (blendToUnselect) => blendToUnselect.isSelected
    );
    if (blendIndex !== undefined && blendIndex >= 0) {
      this.blendSupplierGroups.listOfOptions[blendIndex].isSelected = false;
    }
  }

  allocationValueUpdate(index: number) {
    this.removeBlends();
    const quantity = this.suppliers[index].quantity || 0;
    const percentile =
      quantity > 0 ? Math.floor((quantity * 100) / this.completesNeeded) : 0;
    const selectedSuppliers = this.suppliers.filter(
      ({ setActive }) => setActive
    );
    if (!selectedSuppliers.includes(this.suppliers[index])) {
      selectedSuppliers.push(this.suppliers[index]);
    }
    selectedSuppliers.forEach((supplier) => {
      supplier.quantity = quantity;
      supplier.current_target = quantity;
      supplier.percentile = percentile;
      supplier.flexValue = this._getSupplierFlexibility(supplier, quantity);
    });
    this.totalAllocations = this.getTotalAllocation();
    this.totalRemaining = this.completesNeeded - this.totalAllocations;
    this.calculateSupplierMinMax();
    this._surveyMetaDataService.updateManuallyEditedAllocationStatus(true);
    this.manualAllocationEdit = true;
  }

  unselectBlend() {
    this.selectedBlend = '';
    this.removeSelected();
  }

  private _getSupplierFlexibility(
    supplier: SupplierResponseInterface,
    quantity: number
  ) {
    if (quantity === 0) {
      return 0;
    }
    let supplierFlexValue = supplier.flexValue;
    if (this.flexibilitySelectedType.key !== this.flexibilityStatus[1].key) {
      supplierFlexValue = this.supplierFlex.value;
    }
    return supplierFlexValue;
  }

  allocationPercentileUpdate(index: number) {
    this.removeBlends();
    const percentile = Math.floor(this.suppliers[index].percentile || 0);
    const quantity = Math.ceil(
      (this.suppliers[index].percentile * this.completesNeeded) / 100
    );
    const selectedSuppliers = this.suppliers.filter(
      ({ setActive }) => setActive
    );
    if (!selectedSuppliers.includes(this.suppliers[index])) {
      selectedSuppliers.push(this.suppliers[index]);
    }
    selectedSuppliers.forEach((supplier) => {
      supplier.percentile = percentile;
      supplier.quantity = quantity;
      supplier.completes_goal = quantity;
      supplier.max = quantity;
      supplier.current_target = quantity;
      supplier.flexValue = this._getSupplierFlexibility(supplier, quantity);
    });
    this.totalAllocations = this.getTotalAllocation();
    this.totalRemaining = this.completesNeeded - this.totalAllocations;
    this.calculateSupplierMinMax();
    this._supplierUpdatePayload();
  }

  getTotalAllocation = () => {
    let totalAllocations = 0;
    this.suppliers.forEach((supplier) => {
      totalAllocations += supplier.quantity;
    });
    return totalAllocations;
  };

  openModalGroup() {
    this._modal.open(EditSupplierGroupModalComponent, {
      data: {
        surveyId: this.surveyDetails.ps_survey_id,
        selectedSupplierBlend: this.selectedSupplierGroup,
        suppliers: this.suppliers,
        tempSuppliers: this.tempSuppliers,
        completesNeeded: this.completesNeeded,
        editGroup: this.selectedSupplierGroup.some(
          (selected) => selected.group_ref != undefined
        ),
        cpiOverrideEnabled: !!Number(this.operatorOverrideCpi),
        addSupplierGroup: (
          supplier: SupplierResponseInterface[],
          supplierGroup: SupplierGroupInterface
        ) => this.createGroup(supplier, supplierGroup),
        cpi: Number(this.operatorOverrideCpi),
      },
    });
  }

  clearAllSuppliers() {
    this.removeBlends();
    if (this.totalAllocations > 0) {
      this.suppliers.forEach((supplier) => {
        supplier.quantity = 0;
        supplier.current_target = 0;
        supplier.percentile = 0;
        supplier['min'] = 0;
        supplier['max'] = 0;
        supplier.isChecked = false;
        if (supplier.setActive) {
          delete supplier.setActive;
        }
      });
      this.totalRemaining = this.completesNeeded;
      this.totalAllocations = 0;
    }
    this._supplierUpdatePayload();
  }

  distributeSuppliers() {
    this.supplierIsSelect = false;
    this.suppliers.forEach((supp) => (supp.isChecked = false));
    this.removeBlends();

    if (this.suppliers.some((supp) => supp.setActive === true)) {
      this.suppliers.forEach((supplier) => {
        if (supplier.isValid && supplier.setActive) {
          this.supplierIsSelect = true;
          supplier.isChecked = true;
          this._doSupplierAllocation();
          delete supplier.setActive;
        } else {
          supplier.isChecked = false;
        }
      });
    } else {
      this.suppliers.forEach((supplier) => {
        supplier.isChecked = true;
      });
      this._doSupplierAllocation();
    }
  }

  private _doSupplierAllocation(resetAllocation: boolean = false) {
    this.selectAndDivideAllocation(resetAllocation);
    this.calculateSupplierMinMax();
    this._supplierUpdatePayload();
  }

  selectAndDivideAllocation(resetAllocation: boolean = false) {
    this.totalRemaining = 0;
    this.totalAllocations = 0;
    this.suppliersCount = 0;
    this.totalFeasibility = 0;
    this.suppliers.forEach((supplier) => {
      if (supplier.isValid && supplier.isChecked) {
        this.suppliersCount++;
        if (resetAllocation) {
          supplier.suppliers.forEach((sup) => {
            sup.allocation = this._defaultAllocation;
          });
        }
      }
    });

    // Dividing Allocations
    this.suppliers.forEach((supplier) => {
      if (supplier.isValid && supplier.isChecked) {
        supplier.quantity = Math.floor(
          this.completesNeeded / this.suppliersCount
        );
        this.totalFeasibility += supplier.feasibility || 0;
      } else {
        supplier.quantity = 0;
      }
      this.totalAllocations += supplier.quantity;
    });
    // For distributing the remaining allocation to suppliers
    let remaningCompletes = this.completesNeeded - this.totalAllocations;
    const allocationQuantity = 1;
    if (this.suppliersCount > this.completesNeeded) {
      // PD-10379: more suppliers than completes: set a new rule that any supplier active
      // on a survey at least have an opportunity to send traffic to the survey.
      // The “Total Remaining” value will go negative, but that is acceptable.
      this.suppliers.forEach((supplier) => {
        if (supplier.isValid && supplier.isChecked) {
          supplier.quantity = supplier.quantity + allocationQuantity;
          this.totalAllocations = this.totalAllocations + allocationQuantity;
          remaningCompletes = remaningCompletes - allocationQuantity;
        }
      });
    } else if (this.suppliersCount && remaningCompletes > 0) {
      while (remaningCompletes > 0) {
        this.suppliers.forEach((supplier) => {
          if (remaningCompletes > 0 && supplier.isValid && supplier.isChecked) {
            supplier.quantity = supplier.quantity + allocationQuantity;
            this.totalAllocations = this.totalAllocations + allocationQuantity;
            remaningCompletes = remaningCompletes - allocationQuantity;
          }
        });
      }
    }
    this.totalRemaining = this.completesNeeded - this.totalAllocations;
  }

  selectAllSuppliers() {
    const selection = !this.isAllSuppliersSelected();
    this.suppliers.forEach((supplier) => {
      supplier.isChecked = supplier.setActive = selection;
    });
    this.selectSupplierGroup();
  }

  isAllSuppliersSelected() {
    return this.suppliers.every((s) => s.setActive);
  }

  ungroupSupplierGroup() {
    const selectedGroupIndex = this.suppliers.findIndex((sup) => sup.setActive);
    this.removeBlends();
    if (this.suppliers[selectedGroupIndex]) {
      const ungroupedSuppliers = this.deleteGroup(
        this.suppliers[selectedGroupIndex]
      );
      this.suppliers.splice(selectedGroupIndex, 1, ...ungroupedSuppliers);
      this._processFeasibility();
      this.calculateSupplierMinMax();
      this._supplierUpdatePayload();
    }
    this.showUngroupButton = false;
    this.tempSuppliers = this.suppliers;
  }

  deleteGroup(groupSupp: SupplierResponseInterface) {
    let cpiObject: { [key: number]: string | number } = {};
    this.tempSuppliers.forEach((supplierObj) => {
      if (supplierObj.suppliers.length > 1) {
        supplierObj.suppliers.forEach((supplier) => {
          if (this.eligibleSuppliers) {
            const eligibleSupplier = this.eligibleSuppliers.filter(
              (eligibleSuppliers) => eligibleSuppliers.id === supplier.id
            );
            cpiObject[supplier.id] = eligibleSupplier[0].cpi;
          } else {
            cpiObject[supplier.id] = supplierObj.cpi;
          }
        });
      } else {
        cpiObject[supplierObj.suppliers[0].id] = supplierObj.cpi;
      }
    });
    groupSupp.suppliers.forEach((supp) => {
      supp.cpi = cpiObject[supp.id];
    });
    const suppGoal = groupSupp.completes_goal / groupSupp.suppliers.length;
    let suppGoalRem = groupSupp.completes_goal % groupSupp.suppliers.length;
    const suppQty = Math.floor(groupSupp.quantity / groupSupp.suppliers.length);
    let suppQtyRem = groupSupp.quantity % groupSupp.suppliers.length;
    let groupFlexibility = groupSupp.flexValue;
    return groupSupp.suppliers.map((supp) => {
      const quantity = suppQtyRem > 0 ? suppQty + 1 : suppQty;
      let actual_epc,
        platform_epc,
        estimate_epc,
        variable_margin,
        currency_symbol,
        scpi;
      if (this.loggedInAsServiceBuyer) {
        [
          actual_epc,
          platform_epc,
          estimate_epc,
          variable_margin,
          currency_symbol,
          scpi,
        ] = this._getSingleSupplierMetaData(supp.id);
      }
      supp.allocation = LayoutConstants.DEFAULT_SUPPLIER_ALLOCATION; // PD-13982 resetting the individual supplier allocation
      const ungroupedSupp = {
        group_name: '',
        completes_goal: suppGoalRem > 0 ? suppGoal + 1 : suppGoal,
        current_target: suppGoalRem > 0 ? suppGoal + 1 : suppGoal,
        fielded: 0,
        cpi: supp.cpi,
        block_price: supp.cpi,
        bid_cpi: null,
        suppliers: [supp],
        quantity: quantity,
        flexValue: groupFlexibility,
        is_active: true,
        max: this.completesNeeded,
        isValid: true,
        isChecked: true,
        percentile: quantity
          ? Math.floor((quantity * 100) / this.completesNeeded)
          : 0,
        id: supp.id,
        min: groupSupp.min,
        feasibility: supp.feasibility,
        actual_epc,
        platform_epc,
        estimate_epc,
        variable_margin,
        currency_symbol: groupSupp.currency_symbol,
        scpi,
      };
      suppGoalRem--;
      suppQtyRem--;
      return ungroupedSupp;
    }) as SupplierResponseInterface[];
  }

  private _getSingleSupplierMetaData(supplierId: number) {
    const singleSuppĺier = this.tempSuppliers.find((supplier) =>
      supplier.suppliers.some((s) => s.id === supplierId)
    )!;
    const {
      actual_epc,
      platform_epc,
      estimate_epc,
      variable_margin,
      currency_symbol,
      scpi,
    } = singleSuppĺier;
    return [
      actual_epc,
      platform_epc,
      estimate_epc,
      variable_margin,
      currency_symbol,
      scpi,
    ];
  }

  private _setDefaultPurePriceOverride() {
    if (this.manualOverrideMdl || this.supplierRates) {
      this._deactivateDefaultPurePriceOverride();
    }
  }

  private _deactivateDefaultPurePriceOverride() {
    const defaultPurePriceOverride = new PurePriceOverride(false, 0);
    this._surveyMetaDataService.setPurePriceOverride(defaultPurePriceOverride);
  }

  updateManualOverrideControl() {
    if (this.isAidaEnabled) {
      return; // When AIDA is enabled, do not allow selective manual override
    }

    this.operatorOverrideCpi = '';
    this._sendMcSvpEachUpdateInCache({
      is_pure_price_rate_card_enable: !this.supplierRates,
    });
    if (this.manualOverrideMdl) {
      this.disableOverrideCpiInput = false;
      this.manualOperatorOverride = true;
      this.isOperatorOverideEdit = true;
      this.mcQbOverrideMdl = false;
      this._sendMcSvpOfferPriceUpdateInCache();
      this.setPurepriceRateCardPricing();
      this.validateSelectiveManualOverrideRules('button');
      this._updateConfirmBtnCSS('override', 'remove');
    } else {
      this.isCpiConfirmed = true;
      this.disableOverrideCpiInput = true;
      this.manualOperatorOverride = false;
      this.isOperatorOverideEdit = false;
      this.setPurepriceRateCardPricing();
      this._updateConfirmBtnCSS('selective', 'remove');
    }
    this._setSupplierpriceCardFlag();
    this._setDefaultPurePriceOverride();
  }

  async validateSelectiveManualOverrideRules(
    warning: 'modal' | 'button',
    cpiIndex?: number
  ) {
    let cpiConfirmCounter = 0;
    this.suppliers.forEach((supplier) => {
      let cpivalue = this.loggedInAsServiceBuyer ? supplier.scpi : supplier.cpi;
      const cpiInUsd = this._convertCpiToUsd(supplier.cpi);
      const variableMargin =
        Math.round(
          (((cpiInUsd || 0) - (supplier.scpi || 0)) / (cpiInUsd || 0)) * 100
        ) || 0;
      supplier.variable_margin = Number(variableMargin);
      if (cpivalue && cpivalue > this.maxCpiThershold) {
        this.isCpiConfirmed = false;
        cpiConfirmCounter++;
        this._updateConfirmBtnCSS('selective', 'add');
      } else {
        this.isCpiConfirmed = true;
        this._updateConfirmBtnCSS('selective', 'remove');
      }

      if (cpiConfirmCounter > this.suppliersMaxCpiCounter) {
        this.isCpiConfirmed = false;
        this._updateConfirmBtnCSS('selective', 'add');
      } else {
        this._updateConfirmBtnCSS('selective', 'remove');
        this.isCpiConfirmed = true;
      }
    });
    if (cpiIndex !== undefined) {
      let value = this.loggedInAsServiceBuyer
        ? this.suppliers[cpiIndex].scpi
        : this.suppliers[cpiIndex].cpi;
      if (value && value > this.maxCpiThershold) {
        this.isCpiConfirmed = false;
        cpiConfirmCounter++;

        if (this.loggedInAsServiceBuyer && warning === 'button') {
          this._updateConfirmBtnCSS('selective', 'add');
        }
      } else {
        this.isCpiConfirmed = true;
        this._updateConfirmBtnCSS('selective', 'remove');
      }

      if (
        this.suppliers[cpiIndex].cpi > this.maxCpiThershold &&
        warning === 'modal'
      ) {
        const isConfirmed = await this._modal
          .open(ConfirmationModalComponent, {
            data: {
              cpi_current: this.suppliers[cpiIndex].cpi,
              ps_survey_id: this.surveyDetails.ps_survey_id,
              innerHtml: Constants.CONFIRM_MODAL_TEXT.HIGH_CPI.replace(
                '<CPI>',
                `${this.suppliers[cpiIndex].cpi}`
              ).replace(
                '<CURRENCY_SYMBOL>',
                `${this.surveyDetails.currency_symbol || '$'}`
              ),
              confirmButtonLabel: 'Confirm',
            },
            maxWidth: '25%',
          })
          .onClose$.pipe(map((msg) => msg === 'ok'))
          .toPromise();

        this.isCpiConfirmed = isConfirmed;
        this._updateConfirmBtnCSS('selective', 'remove');

        if (!isConfirmed) {
          this.suppliers[cpiIndex].cpi =
            this.suppliers[cpiIndex].previousCpi ??
            this.suppliers[cpiIndex].block_price;
          this.suppliers.forEach((supplier) => {
            if (supplier.setActive) {
              supplier.cpi = supplier.previousCpi ?? supplier.block_price;
            }
          });
        }
      }
    }

    this._supplierUpdatePayload();
    return true;
  }

  private _shouldShowAidaCpiError(): boolean {
    const isAidaEnabled = !!this.surveyDetails?.aida?.enabled;
    const requiredMinimumCpi = Number(this._eligibleSuppliersDetails?.CPI ?? 0);
    const enteredCpi = Number(this.operatorOverrideCpi);
    const isSurveyNotDraft =
      this.surveyDetails?.ps_survey_status !== SurveyStatus.Draft;

    const isCpiMinimumThanRequired =
      !!this._eligibleSuppliersDetails && enteredCpi < requiredMinimumCpi;
    const isEnteredCpiEmpty = !this.operatorOverrideCpi;

    if (
      (isCpiMinimumThanRequired || isEnteredCpiEmpty) &&
      isSurveyNotDraft &&
      isAidaEnabled
    ) {
      return true;
    }
    return false;
  }

  validateAidaCpi() {
    if (!this.surveyDetails?.aida?.enabled) {
      return;
    }

    const requiredCpi = this._eligibleSuppliersDetails?.CPI;
    const showError = this._shouldShowAidaCpiError();
    this._aidaPayloadService.sendAidaSurveyErrorDetails({
      message: notifyMessage.errorMessage.AIDA.LIVE_CPI_OVERRIDE_CHANGE.replace(
        '<CURRENCY_SYMBOL>',
        this.surveyDetails.currency_symbol
      ).replace('<CPI_VALUE>', requiredCpi?.toString()),
      showError,
    });
  }

  validateOverrideCPIRules = async () => {
    this.validateAidaCpi();
    this._deactivateDefaultPurePriceOverride();
    if (this.operatorOverrideCpi) {
      this.operatorOverrideCpi = String(
        new TruncateNumber(this.operatorOverrideCpi).places(2)
      );
      this.disablesurveyPauseThreshold = true;
      if (Number(this.operatorOverrideCpi) > this.maxCpiThershold) {
        const isConfirmed = await this._modal
          .open(ConfirmationModalComponent, {
            data: {
              innerHtml: Constants.CONFIRM_MODAL_TEXT.HIGH_CPI.replace(
                '<CPI>',
                `${this.operatorOverrideCpi}`
              ).replace(
                '<CURRENCY_SYMBOL>',
                `${this.surveyDetails.currency_symbol || '$'}`
              ),
              cpi_current: this.operatorOverrideCpi,
              ps_survey_id: this.surveyId,
              confirmButtonLabel: 'Confirm',
            },
            maxWidth: '25%',
          })
          .onClose$.pipe(map((msg) => msg === 'ok'))
          .toPromise();
        this.isCpiConfirmed = isConfirmed;
        if (!isConfirmed) {
          this.operatorOverrideCpi = '';
          this.disablesurveyPauseThreshold = false;
          return false;
        }
        this._updateConfirmBtnCSS('all', 'remove');
      } else {
        this._updateConfirmBtnCSS('override', 'remove');
        this.isCpiConfirmed = true;
      }
    } else {
      this.disablesurveyPauseThreshold = false;
    }
    this._sendMcSvpOfferPriceUpdateInCache();
    this._supplierUpdatePayload();
    this.setOverrideCpi();
    return true;
  };

  private _sendHighCpiConfirmation(surveyId: number, cpi: number | string) {
    const activityPayload = new HighCpiConfirmationActivityGenerator(
      surveyId,
      cpi
    ).generate();
    this._activityLogsService.post(activityPayload).subscribe();
  }

  /**
   * update cpi confirm btn css for hide and show confirm btn
   * @Parms cpitype , action
   */
  private _updateConfirmBtnCSS(cpitype: string, action: string) {
    if (action == 'add') {
      this._updateConfirmBtnCSSOnAdd(cpitype);
      this.cpiOverrideConfirmationState = false;
      this.cpiOverrideConfirmationChange.emit(
        this.cpiOverrideConfirmationState
      );
    } else if (action == 'remove') {
      this._updateConfirmBtnCSSOnRemove(cpitype);
      this.cpiOverrideConfirmationState = true;
      this.cpiOverrideConfirmationChange.emit(
        this.cpiOverrideConfirmationState
      );
    }
  }

  private _updateConfirmBtnCSSOnAdd(cpitype: string) {
    if (cpitype == 'override') {
      this.containerConfirmCPIBtn = true;
    } else if (cpitype == 'selective') {
      this.inputSelectiveCPI = true;
      this.containerSelectiveConfirmCPIBtn = true;
    }
  }

  private _updateConfirmBtnCSSOnRemove(cpitype: string) {
    if (cpitype == 'override') {
      this.containerConfirmCPIBtn = false;
    } else if (cpitype == 'selective') {
      this.inputSelectiveCPI = false;
      this.containerSelectiveConfirmCPIBtn = false;
    } else if (cpitype == 'all') {
      this.inputSelectiveCPI = false;
      this.containerSelectiveConfirmCPIBtn = false;
      this.containerConfirmCPIBtn = false;
    }
  }

  setOverrideCpi() {
    if (!isNaN(parseFloat(this.operatorOverrideCpi))) {
      this.setOperatorOverrideCpi();
    } else {
      this.suppliers.forEach((supplier) => {
        if (supplier.group_ref) {
          supplier.cpi =
            this._feasibilityDataService.calculateGroupedSupplierCpi(
              supplier,
              this._eligibleSuppliers
            );
        } else {
          supplier.cpi = supplier.block_price;
        }
      });
    }
    this._sendMcSvpOfferPriceUpdateInCache();
    this._supplierUpdatePayload();
  }

  setOperatorOverrideCpi() {
    if (this.manualOperatorOverride) {
      return;
    }
    if (!Number(this.operatorOverrideCpi)) {
      return;
    }
    this.suppliers.forEach((supplier) => {
      supplier.cpi = Number(parseFloat(this.operatorOverrideCpi).toFixed(2));
    });
  }

  userConfirmedCPI = () => {
    this.isCpiConfirmed = true;
    this._updateConfirmBtnCSS('all', 'remove');
    this._supplierUpdatePayload();
    this.suppliers.forEach((supplier) => {
      if (supplier.scpi && supplier.scpi > Constants.LIMIT_OF_LOW_CPI) {
        this._sendHighCpiConfirmation(
          this.surveyDetails.ps_survey_id,
          supplier.scpi
        );
      }
    });
  };

  private _createGroupObj(
    suppliers: SupplierResponseInterface[],
    groupInfo: {
      fee?: number;
      cpi: number;
      id?: string;
      groupName: string;
      isCustomAllocation: boolean;
      groupSupIds: Array<number>;
      feasibility?: number;
    }
  ) {
    const finalArr = [];
    const supplierWithGroup: SupplierResponseInterface[] = [];
    const supllierWithoutGroup: SupplierResponseInterface[] = [];
    suppliers.forEach((supplier: SupplierResponseInterface) => {
      const supIds = supplier.suppliers.map(({ id }) => id);
      const isExistInGroup = supIds.filter(
        (id: number) => !groupInfo.groupSupIds.includes(id)
      );
      if (isExistInGroup.length) {
        supllierWithoutGroup.push(supplier);
      } else {
        supplierWithGroup.push(supplier);
      }
    });
    if (supplierWithGroup.length) {
      const totalSupplierFeasibility = supplierWithGroup.reduce(
        (acc: number, { feasibility = 0 }) => acc + feasibility,
        0
      );
      const totalCompletes = supplierWithGroup.reduce(
        (acc: number, { completes_goal = 0 }) => acc + completes_goal,
        0
      );
      const totalQuantity = supplierWithGroup.reduce(
        (acc: number, { quantity = 0 }) => acc + quantity,
        0
      );
      const totalFielded = supplierWithGroup.reduce(
        (acc: number, { fielded = 0 }) => acc + fielded,
        0
      );
      const percentile = totalQuantity
        ? Math.floor((totalQuantity * 100) / this.completesNeeded)
        : 0;
      let groupSuppInfo: EachSupplier[] = [];
      supplierWithGroup.forEach((supp: SupplierResponseInterface) => {
        groupSuppInfo = [...groupSuppInfo, ...supp.suppliers];
      });
      const suppGroup: SupplierResponseInterface = {
        group_name: groupInfo.groupName,
        is_custom_allocation: groupInfo.isCustomAllocation,
        completes_goal: totalCompletes,
        current_target: totalCompletes,
        fielded: totalFielded,
        cpi: groupInfo.cpi,
        block_price: groupInfo.cpi,
        feasibility: totalSupplierFeasibility,
        bid_cpi: null,
        suppliers: groupSuppInfo,
        quantity: totalQuantity,
        flexValue: this.supplierFlex.value,
        is_active: true,
        isChecked: true,
        max: this.completesNeeded,
        isValid: true,
        percentile: percentile,
        group_ref: groupInfo.id || '',
        id: 2,
        min: 0,
        currency_symbol: this.surveyDetails.currency_symbol,
        scpi: this._calulateAverageValue(supplierWithGroup, 'scpi'),
        estimate_epc: this._calulateAverageValue(
          supplierWithGroup,
          'estimate_epc'
        ),
        actual_epc: this._calulateAverageValue(supplierWithGroup, 'actual_epc'),
        platform_epc: this._calulateAverageValue(
          supplierWithGroup,
          'platform_epc'
        ),
        fee: groupInfo.fee,
      };
      if (this.isDraftSurvey) {
        suppGroup['group_ref'] = uuidv4();
      }
      finalArr.push(suppGroup);
    }
    return [...finalArr, ...supllierWithoutGroup];
  }

  private _calulateAverageValue(supplierWithGroup: any[], field: string) {
    return Number(
      (
        supplierWithGroup.reduce(
          (acc: number, supplier) => acc + (supplier[field] || 0),
          0
        ) / supplierWithGroup.length
      ).toFixed(2)
    );
  }

  private _applyBlendAllocation(selectedBlend: GetSupplierBlendInterface) {
    this.clearAllSuppliers();

    let singleSupplier: SupplierQuantity[] = new Suppliers(
      this.suppliers
    ).listOfUniqueSuppliers();

    const allocation = AllocationFactory.allocation(
      singleSupplier,
      this.completesNeeded,
      selectedBlend,
      false, //this.theresSupplierWithoutQuantity(checkedSuppliers),
      0 //SHOULD BE remainingCompletes but this variable it's just calculated after all quantityToRedistribute
    );

    for (const index in this.suppliers) {
      let supplierNotFoundInBlend = true;
      const supplier = this.suppliers[index];
      // check if the supplier is individual or a group
      if (supplier.suppliers.length === this.individual_supplier) {
        selectedBlend.suppliers.forEach((suppFromBlend) => {
          // find the individual supplier allocation in the blend (allocation can have value of zero) then apply allocation percentage
          if (
            suppFromBlend.supplier_id.length === this.individual_supplier &&
            suppFromBlend.supplier_id[0].id === supplier.suppliers[0].id &&
            suppFromBlend.allocation_percentage
          ) {
            supplier.percentile = Number(suppFromBlend.allocation_percentage);
            supplier.quantity = allocation.quantity(supplier.suppliers[0].id);
            supplier.completes_goal = allocation.quantity(
              supplier.suppliers[0].id
            );
            supplier.current_target = allocation.quantity(
              supplier.suppliers[0].id
            );
            supplierNotFoundInBlend = false;
          }
        });
      } else if (supplier.suppliers.length >= this.minimumGroupSize) {
        const groupBlend = selectedBlend.suppliers?.find(
          (groupInBlend: { group_name: string }) =>
            groupInBlend.group_name === supplier.group_name
        );
        if (
          groupBlend &&
          groupBlend.allocation_percentage !== '0' &&
          groupBlend.allocation_percentage !== undefined &&
          //@ts-ignore TODO This needs to be fixed in PD-11189
          groupBlend.allocation_percentage !== ''
        ) {
          supplier.percentile = Number(groupBlend.allocation_percentage);
          supplier.quantity = allocation.quantity(supplier.suppliers[0].id);
          supplier.current_target = allocation.quantity(
            supplier.suppliers[0].id
          );
          supplierNotFoundInBlend = false;
        }
      }
      if (supplierNotFoundInBlend) {
        supplier.quantity = allocation.quantity(supplier.suppliers[0].id);
        supplier.current_target = allocation.quantity(supplier.suppliers[0].id);
      }
    }
    this.calculateSupplierMinMax();
    this._supplierUpdatePayload();
  }

  private _setAsSelected(event: GetSupplierBlendInterface) {
    const blendIndex = this.blendSupplierGroups.listOfOptions.findIndex(
      (blendToSelect) => blendToSelect.id === event.id
    );

    this._showSuppliersMissingInBlendWarning();

    if (blendIndex !== undefined && blendIndex >= 0) {
      if (this.blendSupplierGroups.listOfOptions[blendIndex].isSelected) {
        this.unGroupAll();
        this.blendSupplierGroups.listOfOptions[blendIndex].isSelected = false;
        this.getBlendLabel = 'Choose Supplier Blend';
        this.selectedBlend = '';
      } else {
        this.removeSelected();
        this.selectedBlend = this.blendSupplierGroups.listOfOptions[
          blendIndex
        ].isSelected = true;
        this.selectedBlend = this.blendSupplierGroups.listOfOptions[blendIndex];
        this.getBlendLabel =
          this.blendSupplierGroups.listOfOptions[blendIndex].config_name;
      }
    }
  }

  private _showSuppliersMissingInBlendWarning() {
    const supplierAlert = new SupplierBlendAlerts(
      this.selectedBlend,
      this.surveyDetails,
      [],
      this.selectedBlend,
      this.suppliers
    );
    if (supplierAlert.thereIsMissMatchSuppliers()) {
      this._toaster.warning(notifyMessage.warningMessage.MISS_MATCH_SUPPLIERS);
    }
  }

  private _removeZeroAllocationSuppliers(selectedBlend: EachSupplier | any) {
    if (selectedBlend === '' || !selectedBlend.suppliers) {
      return [];
    }
    return selectedBlend.suppliers.filter(
      (supplier: any) => supplier.allocation_percentage != '0'
    );
  }

  private _groupFeasibilityByQuota(
    request?: FeasibilityRequest,
    response?: FeasibilityResponse
  ) {
    let unusedQualifications: string[] = [];
    /** Group response as per request quota category */
    const groupFeasByQuota = response?.quotas.reduce((acc: any, resQuota) => {
      const quotaType = request?.quotas.find(
        (reqQuota) => reqQuota.buyer_quota_id === resQuota.buyer_quota_id
      )?.quota_type;
      if (quotaType) {
        if (quotaType in acc) {
          resQuota.suppliers.forEach((resSup) => {
            acc[quotaType][resSup.supplier_id] =
              (acc[quotaType][resSup.supplier_id] || 0) + resSup.count;
          });
        } else {
          acc[quotaType] = resQuota.suppliers.reduce((innerAcc: any, sup) => {
            // format: { supplier_id: feasibility }
            innerAcc[sup.supplier_id] = sup.count;
            return innerAcc;
          }, {});
        }
      }
      unusedQualifications = [
        ...unusedQualifications,
        ...this._cpiService.mapUnusedQualifications(
          response.unused_qualifications || []
        ),
      ];
      return acc;
    }, {});
    return { groupFeasByQuota, unusedQualifications };
  }

  private _processFeasibility(): void {
    if (this.surveyDetails?.ps_survey_id && this.suppliers?.length) {
      this._getFeasibility().subscribe(
        ({ request, response }) => {
          /** Group response as per request quota category */
          const { groupFeasByQuota, unusedQualifications } =
            this._groupFeasibilityByQuota(request, response);
          if (response?.total_feasibility?.toString()) {
            this.totalFeasibility = response?.total_feasibility;
          }
          if (groupFeasByQuota) {
            // calculate suppliers feasibility
            this._applySuppliersFeasibility(
              groupFeasByQuota,
              unusedQualifications
            );
          }
          if (response?.pricing_multplier) {
            this.isPriceMultiplierAdded = true;
            this._priceMultiplier = response!.pricing_multplier;
          }
        },
        (err) => {
          throw this._toaster.error(
            notifyMessage.errorMessage.FEASIBILITY_PROCESSING
          );
        }
      );
    }
  }

  private _getFeasibility(): Observable<FeasibilityData> {
    if (this.surveyDetails.ps_survey_status !== SURVEY_STATUS.DRAFT) {
      const { ps_survey_id } = this.surveyDetails;
      const supplier_ids = this._feasibilityService.mapSupplierIds(
        this.suppliers
      );

      const qualification = this._qualificationService.getQualifications();
      const { request } = this._feasibilityService.buildFeasibilityPayload(
        qualification,
        this.surveyDetails as unknown as SurveyRequest, // A temporary hack, but I guess, since 'SurveyRequest' and 'BuyerSurveyResponse', we need to do something about it
        ps_survey_id,
        supplier_ids
      );
      return forkJoin({
        request: of(request),
        response: this._feasibilityService.getFeasibility(request),
      });
    } else {
      return this._surveyMetaDataService.getFeasibilityData();
    }
  }

  private _applySuppliersFeasibility(
    groupFeasByQuota: GroupedByQuotaFeas,
    unusedQualifications: string[]
  ): void {
    for (const supplierObj of this.suppliers) {
      let minSupplierFeasibility: number = 0;
      for (const supplier of supplierObj.suppliers) {
        const supFeasArr = Object.values(groupFeasByQuota).map(
          (elem) => elem[supplier.id] || 0
        );
        minSupplierFeasibility =
          minSupplierFeasibility + Math.min(...supFeasArr);
        supplier.feasibility = Math.min(...supFeasArr);
      }
      if (minSupplierFeasibility) {
        supplierObj.feasibility = minSupplierFeasibility;
        supplierObj.unUsedQual = String([...new Set(unusedQualifications)]);
      }
    }
  }

  surveyPauseThresholdUpdate() {
    this._supplierUpdatePayload();
  }

  blendId(selectedBlend: GetSupplierBlendInterface): string {
    if (selectedBlend?.id) {
      return selectedBlend?.id;
    }
    return '';
  }

  private _supplierUpdatePayload() {
    let payload = {
      suppliers: deepCopy(this.suppliers),
      flexValue: this.supplierFlex.value,
      isCpiConfirmed: this.isCpiConfirmed,
      totalAllocations: this.totalAllocations,
      completesNeeded: this.completesNeeded,
      operatorOverrideMdl: Number(this.operatorOverrideCpi) || 0,
      manualOverrideMdl: this.manualOverrideMdl,
      surveyPauseThreshold: this.surveyPauseThreshold,
      blend_id: this.blendId(this.selectedBlend),
      isPriceMultiplierAdded: this.isPriceMultiplierAdded,
      manualScpiOverrideEnabled: this.manualScpiOverrideEnabled,
      isPurePriceRateCardEnable: this.isPurePriceRatesEnabled,
      mcQbOverrideMdl: this.mcQbOverrideMdl,
    };
    this._surveyMetaDataService.setSupplierListPayload(payload);
    this._surveyMetaDataService.setMcQbOverrideValue(this.mcQbOverrideMdl);
    this._surveyMetaDataService.setSupplierOverrideValue(
      this.manualScpiOverrideEnabled
    );
  }

  applyGlobalFlexibility(event: GetSupplierBlendInterface) {
    if (
      !(
        event.blend_flexibility === null ||
        event.blend_flexibility === undefined
      )
    ) {
      this.supplierFlex.value = event.blend_flexibility;
      this.updateFlexValue(event.blend_flexibility);
    }
  }

  applyNoneFlexibility() {
    this.supplierFlex.value = 0;
    this.updateFlexValue(0);
  }

  getSupplierFlexibility(event: GetSupplierBlendInterface, id: number): number {
    return Number(
      event.suppliers.find((supplier) => Number(supplier.id) === id)
        ?.flexibility_percentage ?? 100
    );
  }

  getGroupFlexibility(
    event: GetSupplierBlendInterface,
    group_name: string
  ): number {
    return Number(
      event.suppliers.find((supplier) => supplier.group_name === group_name)
        ?.flexibility_percentage ?? 100
    );
  }

  applySpecificFlexibility(event: GetSupplierBlendInterface) {
    this.suppliers.forEach((supp) => {
      if (supp.group_name) {
        supp.flexValue = this.getGroupFlexibility(event, supp.group_name);
      } else {
        supp.flexValue = this.getSupplierFlexibility(
          event,
          supp.suppliers[0].id
        );
      }
    });
  }

  private _applyBlendFlexibility(event: GetSupplierBlendInterface) {
    if (event.flexibilitySelectedType) {
      switch (event.flexibilitySelectedType.key) {
        case FLEXIBILITY_OPTIONS.GLOBAL.key:
          this.flexibilitySelectedType = FLEXIBILITY_OPTIONS.GLOBAL;
          this.applyGlobalFlexibility(event);
          break;
        case FLEXIBILITY_OPTIONS.NONE.key:
          this.flexibilitySelectedType = FLEXIBILITY_OPTIONS.NONE;
          this.applyNoneFlexibility();
          break;
        case FLEXIBILITY_OPTIONS.SPECIFIC.key:
          this.flexibilitySelectedType = FLEXIBILITY_OPTIONS.SPECIFIC;
          this.applySpecificFlexibility(event);
          break;
        default:
          break;
      }
    } else {
      this.applyGlobalFlexibility(event);
    }
    this.calculateSupplierMinMax();
  }

  private _resetFlexibility() {
    this.supplierFlex.value = this._defaultFlexibility;
    this.flexibilitySelectedType = FLEXIBILITY_OPTIONS.GLOBAL;
    this.updateFlexValue(this._defaultFlexibility);
    this.removeBlends();
  }

  removeBlends() {
    this.selectedBlend = '';
    if (this.surveyDetails) {
      this.surveyDetails.blend_id = '';
    }
    this.removeSelected();
  }

  selectInput(e: Event) {
    this.suppliers.forEach((supplier) => {
      supplier.previousCpi = supplier.cpi;
    });
    e.stopPropagation();
    (e.target as HTMLInputElement).select();
  }

  formatCpi(cpi: number, i: number) {
    if (this.suppliers[i].cpi) {
      this.suppliers[i].cpi = Number(
        this._decimalPipe.transform(cpi, '2.0-2')?.replace(',', '')
      );
    }
  }

  private _setSupplierValidFlag() {
    this.suppliers.forEach((supplier) => {
      if (supplier.cpi == 0 && !supplier.group_ref) {
        supplier.isValid = false;
      } else {
        supplier['isValid'] = supplier.is_active === false ? false : true;
      }
    });
  }

  public updateManualScpiOverrideControl() {
    if (this.isAidaEnabled) {
      return; // When AIDA is enabled, do not allow selective manual override
    }

    if (!this.manualScpiOverrideEnabled) {
      this._resetSupplierCpiWithVariableMargin();
      this._updateConfirmBtnCSS('selective', 'remove');
    } else {
      this.validateSelectiveManualOverrideRules('button');
    }
    this._supplierUpdatePayload();
  }
  //Buyer CPI (USD) - (Buyer CPI(USD) * margin)
  private _resetSupplierCpiWithVariableMargin() {
    const margin = this.surveyDetails.margin_override!.margin;
    for (const supplier of this.suppliers) {
      const usdCpi = this._convertCpiToUsd(supplier.cpi);
      const newSupplierCpi = usdCpi - (usdCpi * margin) / 100;
      supplier.scpi = Number(newSupplierCpi.toFixed(2));
    }
  }

  private _setUpOperatorOverridesFlags() {
    this.showSupplierManualOverride =
      this.loggedInAsServiceBuyer &&
      this.surveyDetails.is_enable_margin_override;
    this.hidePricesAcordion =
      this.surveyDetails.is_enable_margin_override &&
      !this.loggedInAsServiceBuyer;
  }

  private _setPurePriceRateCardData(surveyData: BuyerSurveyResponse) {
    this._surveyMetaDataService.setPurePriceRateCardEnable(
      surveyData.is_pure_price_rate_card_enable
    );
  }

  private _setSupplierpriceCardFlag() {
    if (this.isPurePriceRatesEnabled) {
      this.showSupplierCPIColumn =
        (!this.showSupplierManualOverride && this.manualOverrideMdl) ||
        this.supplierRates;
    } else {
      this.showSupplierCPIColumn = !this.showSupplierManualOverride;
    }
  }

  private _calculateSurveyPauseThreshold() {
    const supplierCpi = this.suppliers.reduce(
      (partialSum, supplier) => partialSum + supplier.cpi * supplier.max,
      0
    );
    const supplierMax = this.suppliers.reduce(
      (partialSum, supplier) => partialSum + supplier.max,
      0
    );
    return Number((supplierCpi / supplierMax).toFixed(2));
  }

  public openBlendModal() {
    this._modal
      .open(BlendModalComponent, {
        data: {
          suppliers: this.suppliers,
          blendFlexibility: this.supplierFlex.value,
          flexibilitySelectedType: this.flexibilitySelectedType,
        },
        width: '400px',
      })
      .onClose$.subscribe((newBlend: GetSupplierBlendInterface) => {
        if (newBlend) {
          const idx = this.supplierBlendData?.findIndex(
            (b) => b.id === newBlend.id
          );
          if (idx && idx > -1) {
            this.supplierBlendData![idx] = newBlend;
          } else {
            this.supplierBlendData?.push(newBlend);
          }
          this.getSuppliersBlends();
          this.selectBlend(newBlend);
        }
      });
  }

  private _isMcSvpSurvey() {
    return !!this.surveyDetails?.mc?.SVP;
  }

  public setMcQbOverrideMdl() {
    this.setOverrideCpi();
  }

  private _handleMcSvpChildSurveyProcessing() {
    const childSurveys$ = this._multiCountryDraftService
      .getMcSvpUpdatedSurveysResponse()
      .pipe(
        filter((data) => !!data.length),
        map((data) => new McSvpParentChildSegregator(data).childs())
      );

    this._subscriptions.add(
      childSurveys$.subscribe((childSurveys) => {
        this.mcSpvChildsDropdownData =
          this._mapMcSvpSurveysResponseIntoDropdownData(childSurveys);
        this.selectedMcSpvChild = this._selectedMcSpvChildSurvey();
        this.surveyDetails = deepCopy(this._selectedMcSpvChildSurvey());
        this.surveyId = this.surveyDetails.ps_survey_id;
        this.completesNeeded = Math.round(
          this._getCompletesNeeded(this.surveyDetails)
        );
        this._processSurveyData(this.surveyDetails, false, false);
        this._processSupplierBlendData(this.supplierBlendData || []);
      })
    );

    this._subscriptions.add(
      this._eventEmitterService.mcSvpSuppliersDataSaved.subscribe(async () => {
        await this._processNewLocaleDataAfterPreviousSaved();
      })
    );
  }

  private _mapMcSvpSurveysResponseIntoDropdownData(
    surveys: BuyerSurveyResponse[]
  ): CountrySurveyData[] {
    const mappedSurveys = surveys.map((survey) => {
      return {
        ...survey,
        country_name: survey.locale.country_name,
        language_code: survey.locale?.language_translate.toUpperCase(),
      };
    });
    return new LocaleSorter(mappedSurveys).sort();
  }

  public selectMcSvpChild(data: CountrySurveyData) {
    // When already selected survey is re-selected, do not do anything
    if (data.ps_survey_id === this.surveyId) {
      return;
    }
    const selected = this.mcSpvChildsDropdownData.find(
      (item) => item.ps_survey_id === data.ps_survey_id
    )!;
    this.selectedMcSpvChild = selected;
    this.selectedMcSvpSurvey.emit(selected);
  }

  private async _processNewLocaleDataAfterPreviousSaved() {
    const surveyData = this._mapMcSvpDropdownDataInoBuyerResponse(
      this.selectedMcSpvChild
    );
    this.surveyDetails = deepCopy(surveyData);
    this.surveyId = this.surveyDetails.ps_survey_id;
    this._resetOverridesBeforeProcessingNewSelection();
    this._processSurveyData(this.surveyDetails, true, false);
    this.lang = this.surveyDetails.locale.language_code;
    this.loi = this.surveyDetails.expected_loi;
    this.ir = this.surveyDetails.expected_ir;
    this.fieldTime = this.surveyDetails.field_time;
    this.completesNeeded = Math.round(
      this._getCompletesNeeded(this.surveyDetails)
    );
    this.supplierBlendData = [];
    this.getFlexibilityPricesData(this.surveyId, true);
    this.getSuppliers(this.surveyDetails.ps_survey_id!, true).then(() => {
      this._patchSupplierOverridesOfSelectedMcSvpDraftSurvey(
        this.surveyDetails
      );
      this._processSupplierBlendData(this.supplierBlendData || []);
      if (this.totalRemaining > 0) {
        this._doSupplierAllocation();
      }
    });
  }

  private _mapMcSvpDropdownDataInoBuyerResponse(
    source: CountrySurveyData
  ): BuyerSurveyResponse {
    const { language_code, country_name, ...buyerSurveyResponse } = source;
    return buyerSurveyResponse;
  }

  private _selectedMcSpvChildSurvey(): CountrySurveyData {
    const found = this.mcSpvChildsDropdownData.find(
      (survey) => survey.ps_survey_id === this.surveyId
    );

    if (!found) {
      return this.mcSpvChildsDropdownData[0];
    }

    return found;
  }

  private _getCompletesNeeded(surveyData: BuyerSurveyResponse) {
    return surveyData.click_balancing === 1
      ? surveyData.estimated_clicks!
      : surveyData.completes_required!;
  }

  private _patchSupplierOverridesOfSelectedMcSvpDraftSurvey(
    survey: BuyerSurveyResponse
  ) {
    if (survey.ps_survey_status !== SURVEY_STATUS.DRAFT) {
      return;
    }
    this.operatorOverrideCpi = survey.offer_price
      ? String(survey.offer_price)
      : '';
    this._supplierUpdatePayload();
  }

  private _sendMcSvpOfferPriceUpdateInCache() {
    if (!this._isMcSvpSurvey()) {
      return;
    }

    if (!this.operatorOverrideCpi) {
      this._sendMcSvpEachUpdateInCache({
        offer_price: Number(this.operatorOverrideCpi),
      });
      return;
    }

    if (this.mcQbOverrideMdl) {
      this._sendMcSvpAllUpdateInCache({
        offer_price: Number(this.operatorOverrideCpi),
      });
      return;
    }
    this._sendMcSvpEachUpdateInCache({
      offer_price: Number(this.operatorOverrideCpi),
    });
  }

  private _sendMcSvpAllUpdateInCache(data: Partial<BuyerSurveyResponse>) {
    const cache = {
      for: 'all' as const,
      data,
    };
    this._multiCountryDraftService.sendMcSpvUpdateInCacheCommand(cache);
  }

  private _sendMcSvpEachUpdateInCache(data: Partial<BuyerSurveyResponse>) {
    const cache = {
      for: 'each' as const,
      data: [
        {
          survey_localization: this.surveyDetails.survey_localization,
          ...data,
        },
      ],
    };
    this._multiCountryDraftService.sendMcSpvUpdateInCacheCommand(cache);
  }

  private _resetOverridesBeforeProcessingNewSelection(): void {
    this.operatorOverrideCpi = '';
    this.operatorOverrideCPI = '';
    this.selectivePriceOveride = false;
    this.manualOverrideMdl = false;
    this.disableOverrideCpiInput = true;
    this.manualOperatorOverride = false;
    this.isOperatorOverideEdit = false;
  }

  private _handleMcSvpGlobalOverrideToAllCheckbox(): void {
    this._subscriptions.add(
      this._surveyMetaDataService.getMcQbOverrideValue().subscribe((value) => {
        this.mcQbOverrideMdl = value;
      })
    );
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }
}
